/* eslint-disable camelcase */
import { observer } from "mobx-react-lite"
import React, { FC, useCallback } from "react"
import { Appearance, useWindowDimensions, ViewStyle } from "react-native"
import {
  FlatList, ListEmpty,
  ListRenderItem,
  ListRenderItemInfo,
  Screen,
  Text,
} from "../../../../components"
import numeral from "numeral"
import { useStores } from "../../../../models"
import { BatchStoreModel } from "../../../../models/app-store/farm-store/batch-store/batch-store"
import { FarmStoreModel } from "../../../../models/app-store/farm-store/farm-store"
import { color, nativeBaseTheme } from "../../../../theme"
import { SafeAreaView } from "react-native-safe-area-context"
import { View } from "native-base"
import _ from "lodash"
import { translate } from "../../../../i18n"
import { useFocusEffect } from "@react-navigation/native"
import JalaAnalytic from "../../../../utils/jala-analytic"
import { InfoWarningModal } from "../overview-feed-cost-list/overview-feed-cost-list-screen"
import { getSnapshot } from "mobx-state-tree"

const ROOT: ViewStyle = {
  backgroundColor: color.palette.white,
  flex: 1,
}

const HEADER: ViewStyle = {
  borderTopWidth: 1,
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  flexDirection: 'row',
  paddingVertical: 8,
  alignItems: 'center'
}

const CONTENT_ITEM: ViewStyle = {
  borderBottomWidth: 1,
  borderColor: color.palette.gray200,
  paddingVertical: 10,
  flexDirection: 'row'
}

const Header: FC<{ colorScheme?: "light"|"dark" }> = observer(function Header(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { actualFeedAccumulation, cycles, isFeedActualWithin4Days, loadingCycleActual } = activeBatchStore || BatchStoreModel.create({})

  return (
    <View paddingX="16px" paddingTop="8px" paddingBottom="15px">
      <Text text={farmStore.farm.name} preset="title3" marginBottom="4px" colorScheme={colorScheme} />
      <Text tx="overviewFeedAccumulationActualListScreen.subtitle" preset="body3" colorScheme={colorScheme} color={nativeBaseTheme.colors.gray[colorScheme === 'light' ? '700' : '300']} />
      <View flexDirection="row" alignItems="center">
        <Text
          text={numeral(actualFeedAccumulation).format('0,0.[00]')}
          preset="display1"
          marginRight="3px"
          colorScheme={colorScheme}
          color={loadingCycleActual && color.palette.dust500 || undefined}
        />
        <Text text="kg" preset="display2" marginRight="8px" colorScheme={colorScheme} color={color.palette.dust500} />
      </View>
      <Text text={translate("overviewScreen.fromActivePonds", { count: cycles.length })} preset="body3" colorScheme={colorScheme} color={color.palette.dust500} />
      {!isFeedActualWithin4Days && <InfoWarningModal />}
    </View>
  )
})

const TableHeader: FC<{ colorScheme?: "light"|"dark" }> = (props) => {
  const { colorScheme } = props;

  return (
    <View style={HEADER}>
      <View width="10%" paddingX="8px" alignItems="flex-start">
        <Text text="#" preset="body3" transformText={_.upperFirst} colorScheme={colorScheme} />
      </View>
      <View width="30%" paddingX="8px" alignItems="flex-start">
        <Text tx="cyclePredictions.pond" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="15%" paddingX="8px" alignItems="flex-start">
        <Text tx="cyclePredictions.age" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="20%" paddingX="4px" alignItems="flex-start">
        <Text tx="cyclePredictions.today" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="25%" paddingX="4px" alignItems="flex-start">
        <Text tx="cyclePredictions.total" transformText={_.upperFirst} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
}

const Item: FC<{ colorScheme?: "light" | "dark", info: ListRenderItemInfo<any> }> = observer(function Item(props) {
  const { colorScheme, info } = props
  const { index, item: { age, pond_name, cycle_actuals } } = info;
  const { feed, feed_accumulation } = cycle_actuals.find((c) => c.age <= age) || {};

  return (
    <View style={CONTENT_ITEM}>
      <View width="10%" paddingX="8px" alignItems="flex-start">
        <Text text={`${index + 1}`} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="30%" paddingX="8px" alignItems="flex-start">
        <Text text={pond_name} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="15%" paddingX="8px" alignItems="flex-start">
        <Text text={`${age}`} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="20%" paddingX="4px" alignItems="flex-start">
        <Text text={numeral(feed).format('0,0.[00]')} preset="body3" colorScheme={colorScheme} />
      </View>
      <View width="25%" paddingX="4px" alignItems="flex-start">
        <Text text={numeral(feed_accumulation).format('0,0.[00]')} preset="body3" colorScheme={colorScheme} />
      </View>
    </View>
  )
});

export const OverviewFeedAccumulationActualListScreen: FC<{ colorScheme?: "light" | "dark" }> = observer(function OverviewFeedAccumulationActualList(props) {
  const { colorScheme = "light" || Appearance.getColorScheme() } = props;
  const { height } = useWindowDimensions();

  // Pull in one of our MST stores
  const { appStore: { farmStore }} = useStores();
  const { activeBatchStore } = farmStore || FarmStoreModel.create({})
  const { cycles, loadingCycleActual } = activeBatchStore || BatchStoreModel.create({})

  const keyExtractor = useCallback((item, index) => `${item.id || item.started_at || index}`, [])

  const renderItem: ListRenderItem<any> = useCallback((info) => {
    return <Item info={info} colorScheme={colorScheme} />
  }, [colorScheme])

  const onRefresh = useCallback(() => {
    activeBatchStore.init()
  }, [activeBatchStore])

  useFocusEffect(useCallback(() => {
    JalaAnalytic.log('Mobile app: home - overview - total pakan');
  }, []))

  return (
    <Screen
      style={ROOT}
      preset="fixed"
      unsafe
      colorScheme={colorScheme}
      backgroundBar={colorScheme === "light" ? color.primary : undefined}
      statusBar={colorScheme === 'light' ? 'light-content' : 'dark-content'}
      hideOnBlurred={false}
    >
      <FlatList
        data={getSnapshot(cycles)}
        renderItem={renderItem}
        keyExtractor={keyExtractor}
        ListHeaderComponent={<>
          <Header colorScheme={colorScheme} />
          <TableHeader colorScheme={colorScheme} />
        </>}
        ListEmptyComponent={<ListEmpty colorScheme={colorScheme} height={`${height - 270}px`} />}
        ListFooterComponent={<SafeAreaView edges={['bottom']} />}
        onRefresh={onRefresh}
        refreshing={!!loadingCycleActual}
        estimatedItemSize={37}
      />
    </Screen>
  )
})
